import { useEffect, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { PhotoThumb } from "../equipment/shared/PhotoThumb";
import BulletsItemForm from "./BulletsItemForm";
import { HoEntryTag } from "./shared/HoManualEntryFields";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
const fmtTime = (t) => (t ? new Date(t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—");

export function BulletsStatsTable({ stats }) {
  const types = Object.keys(stats || {});
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="bullets-stats-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Bullet Type</TableHead>
            <TableHead>Previous Stock</TableHead>
            <TableHead>Received Today</TableHead>
            <TableHead>Replaced Today</TableHead>
            <TableHead>Total Stock</TableHead>
            <TableHead>Balance</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {types.map((t) => {
            const s = stats[t];
            return (
              <TableRow key={t} data-testid={`bullets-stats-row-${t}`}>
                <TableCell className="font-medium">{t}</TableCell>
                <TableCell className="font-mono">{fmt(s.previous_stock)}</TableCell>
                <TableCell className="font-mono">{fmt(s.received_today)}</TableCell>
                <TableCell className="font-mono">{fmt(s.replaced_today)}</TableCell>
                <TableCell className="font-mono">{fmt(s.total_stock)}</TableCell>
                <TableCell className={`font-mono font-semibold ${s.balance < 0 ? "text-red-600" : "text-emerald-700"}`} data-testid={`bullets-balance-${t}`}>
                  {fmt(s.balance)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function BulletsDayDetail({ dayId, onClose, onChanged }) {
  const [day, setDay] = useState(null);
  const [form, setForm] = useState(null);

  useEffect(() => {
    client
      .get(`/bullets-register/day/${dayId}`)
      .then((res) => setDay(res.data))
      .catch((err) => {
        toast.error(getErrorMessage(err));
        onClose();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayId]);

  const applyUpdate = (updated) => {
    setDay(updated);
    if (updated.negative_balance_warning) {
      toast.warning(`Bullets balance has gone negative for ${updated.negative_balance_type}. Please verify the entries.`);
    }
    onChanged?.();
  };

  const remove = async (kind, itemId) => {
    if (!window.confirm("Remove this line-item? This cannot be undone.")) return;
    try {
      const res = await client.delete(`/bullets-register/day/${dayId}/${kind}/${itemId}`);
      applyUpdate(res.data);
      toast.success("Entry removed");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (!day) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent data-testid="bullets-day-detail-loading">
          <p className="py-8 text-center text-slate-400">Loading...</p>
        </DialogContent>
      </Dialog>
    );
  }

  const editable = day.editable;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="bullets-day-detail">
        <DialogHeader>
          <DialogTitle>
            {day.display_label} — {new Date(day.date).toLocaleDateString("en-IN")}
          </DialogTitle>
        </DialogHeader>

        <BulletsStatsTable stats={day.stats} />

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">Received ({day.received_items.length})</h3>
            {editable && (
              <Button size="sm" data-testid="bullets-add-received" onClick={() => setForm({ kind: "received", initial: null })} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Received
              </Button>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table data-testid="bullets-received-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Remarks</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Photos</TableHead>
                  {editable && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {day.received_items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-4 text-slate-400 text-sm">
                      No received entries.
                    </TableCell>
                  </TableRow>
                ) : (
                  day.received_items.map((it) => (
                    <TableRow key={it.item_id} data-testid={`bullets-received-row-${it.item_id}`}>
                      <TableCell className="text-xs">{fmtTime(it.time)}</TableCell>
                      <TableCell className="text-xs font-medium">{it.bullet_type}</TableCell>
                      <TableCell className="font-mono">{fmt(it.quantity)}</TableCell>
                      <TableCell className="text-xs max-w-[140px] truncate">{it.remarks || "—"}</TableCell>
                      <TableCell className="text-xs">{it.entered_by_name || it.entered_by}{it.ho_entry && <HoEntryTag />}</TableCell>
                      <TableCell>
                        <div className="flex gap-1.5">
                          {(it.photos || []).map((_, i) => (
                            <PhotoThumb key={i} path={`/bullets-register/day/${dayId}/received/${it.item_id}/photo/${i}`} alt="received" testId={`bullets-received-photo-${it.item_id}-${i}`} size="h-10 w-10" />
                          ))}
                        </div>
                      </TableCell>
                      {editable && (
                        <TableCell className="text-right space-x-1.5">
                          <Button size="sm" variant="outline" data-testid={`bullets-received-edit-${it.item_id}`} onClick={() => setForm({ kind: "received", initial: it })}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="destructive" data-testid={`bullets-received-delete-${it.item_id}`} onClick={() => remove("received", it.item_id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">Replacements ({day.replacement_items.length})</h3>
            {editable && (
              <Button size="sm" data-testid="bullets-add-replacement" onClick={() => setForm({ kind: "replacement", initial: null })} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Replacement
              </Button>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table data-testid="bullets-replacement-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Tool Used</TableHead>
                  <TableHead>No. of Bullets</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Old Photos</TableHead>
                  <TableHead>New Photos</TableHead>
                  {editable && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {day.replacement_items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-4 text-slate-400 text-sm">
                      No replacement entries.
                    </TableCell>
                  </TableRow>
                ) : (
                  day.replacement_items.map((it) => (
                    <TableRow key={it.item_id} data-testid={`bullets-replacement-row-${it.item_id}`}>
                      <TableCell className="text-xs">{fmtTime(it.time)}</TableCell>
                      <TableCell className="text-xs font-medium">{it.bullet_type}</TableCell>
                      <TableCell className="text-xs">{it.tool_used}</TableCell>
                      <TableCell className="font-mono">{fmt(it.number_of_bullets)}</TableCell>
                      <TableCell className="text-xs">{it.entered_by_name || it.entered_by}{it.ho_entry && <HoEntryTag />}</TableCell>
                      <TableCell>
                        <div className="flex gap-1.5">
                          {(it.old_photos || []).map((_, i) => (
                            <PhotoThumb key={i} path={`/bullets-register/day/${dayId}/replacement/${it.item_id}/old-photo/${i}`} alt="old bullet" testId={`bullets-old-photo-${it.item_id}-${i}`} size="h-10 w-10" />
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1.5">
                          {(it.new_photos || []).map((_, i) => (
                            <PhotoThumb key={i} path={`/bullets-register/day/${dayId}/replacement/${it.item_id}/new-photo/${i}`} alt="new bullet" testId={`bullets-new-photo-${it.item_id}-${i}`} size="h-10 w-10" />
                          ))}
                        </div>
                      </TableCell>
                      {editable && (
                        <TableCell className="text-right space-x-1.5">
                          <Button size="sm" variant="outline" data-testid={`bullets-replacement-edit-${it.item_id}`} onClick={() => setForm({ kind: "replacement", initial: it })}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="destructive" data-testid={`bullets-replacement-delete-${it.item_id}`} onClick={() => remove("replacement", it.item_id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {form && (
          <BulletsItemForm
            kind={form.kind}
            dayId={dayId}
            initial={form.initial}
            onClose={() => setForm(null)}
            onSaved={(updated) => {
              setForm(null);
              applyUpdate(updated);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
